<script setup lang="ts">
  import {
    ListboxContent,
    ListboxFilter,
    ListboxGroup,
    ListboxGroupLabel,
    ListboxItem,
    ListboxItemIndicator,
    ListboxRoot,
    useFilter,
  } from 'reka-ui';
  import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

  import type { Contact, Partner, User } from '@stuut/api/models';
  import { Badge } from '@ui/components/badge';
  import type { BadgeVariants } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/components/hover-card';
  import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import {
    TagsInput,
    TagsInputInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
  } from '@ui/components/tags-input';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { isEmailValid } from '@ui/utils/utils';

  import { ContactEditorDialog } from '@/components/contacts';
  import { useFeatureFlag } from '@/components/feature-flags';
  import { UserAvatar } from '@/components/users';
  import {
    mapInternalContacts,
    useInternalContactsList,
    usePartnerInternalContacts,
  } from '@/composables/useInternalContacts';
  import type { InternalContactOption } from '@/composables/useInternalContacts';

  type ContactGroupKey = 'customer' | 'internal' | 'team';

  const props = withDefaults(
    defineProps<{
      partner?: Partner;
      users?: User[];
      invalid?: boolean;
      disabled?: boolean;
      placeholder?: string;
      suggestContacts?: boolean;
      excludeGroups?: ContactGroupKey[];
    }>(),
    {
      users: () => [],
      placeholder: 'Add email...',
      excludeGroups: () => [],
    },
  );

  const emit = defineEmits<{
    (e: 'blur'): void;
  }>();

  const selectedEmails = defineModel<string[]>({ default: () => [] });
  const searchTerm = ref('');
  const open = ref(false);
  const openedByChevron = ref(false);
  const inputRef = useTemplateRef<HTMLInputElement>('inputRef');
  const newContactEmail = ref<string | undefined>(undefined);
  const isContactEditorOpen = ref(false);
  const hasError = ref(false);

  const { contains } = useFilter({ sensitivity: 'base' });

  const { value: icFlag, ready: icReady } = useFeatureFlag('internal-contacts-ui');
  const isInternalContactsEnabled = computed(() => icReady.value && Boolean(icFlag.value));

  // Internal contacts come from two endpoints: those assigned to this partner
  // (plus org-wide/floating ones) and the org's full internal-contacts list.
  // Merge both, partner-assigned taking precedence, deduped by email.
  const { options: partnerInternalOptions } = usePartnerInternalContacts(() => props.partner?.id, {
    enabled: isInternalContactsEnabled,
  });
  const { data: orgInternalData } = useInternalContactsList({
    enabled: isInternalContactsEnabled,
  });
  const orgInternalOptions = computed(() => mapInternalContacts(orgInternalData.value?.data ?? []));

  const v2InternalOptions = computed(() => {
    const seen = new Set<string>();
    const merged: InternalContactOption[] = [];

    for (const contact of [...partnerInternalOptions.value, ...orgInternalOptions.value]) {
      const key = contact.email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(contact);
    }

    return merged;
  });

  const isAlreadySelected = (email: string) =>
    (selectedEmails.value ?? []).some((e) => e.toLowerCase() === email.toLowerCase());

  const contactsByEmail = computed(() => {
    const map = new Map<string, Contact | User | InternalContactOption>();
    (props.partner?.allContacts ?? []).forEach((c) => {
      if (c.email) map.set(c.email.toLowerCase(), c);
    });
    (props.users ?? []).forEach((u) => {
      if (u.email) map.set(u.email.toLowerCase(), u);
    });
    if (isInternalContactsEnabled.value) {
      v2InternalOptions.value.forEach((c) => {
        const key = c.email.toLowerCase();
        if (!map.has(key)) map.set(key, c);
      });
    }
    return map;
  });

  const findContact = (email: string): Contact | User | InternalContactOption | undefined =>
    contactsByEmail.value.get(email.toLowerCase());

  const GROUP_BADGE: Record<ContactGroupKey, { label: string; variant: BadgeVariants['variant'] }> =
    {
      customer: { label: 'Customer', variant: 'outline' },
      internal: { label: 'Internal', variant: 'primary' },
      team: { label: 'Team', variant: 'secondary' },
    };

  // Classify an email by the option group it belongs to, mirroring `contactsByEmail`
  // precedence (team users > customer/internal contacts > v2 internal contacts) so the
  // badge stays consistent with the contact shown in the hover card.
  const groupForEmail = (email: string): ContactGroupKey | undefined => {
    const lower = email.toLowerCase();

    if ((props.users ?? []).some((u) => u.email?.toLowerCase() === lower)) return 'team';

    const contact = (props.partner?.allContacts ?? []).find(
      (c) => c.email?.toLowerCase() === lower,
    );
    if (contact) return contact.internal_role ? 'internal' : 'customer';

    if (
      isInternalContactsEnabled.value &&
      v2InternalOptions.value.some((c) => c.email.toLowerCase() === lower)
    ) {
      return 'internal';
    }

    return undefined;
  };

  const taggedItems = computed(() =>
    (selectedEmails.value ?? []).map((email) => ({
      email,
      contact: findContact(String(email)),
      group: groupForEmail(String(email)),
    })),
  );

  const handleAddContact = (email: string) => {
    newContactEmail.value = email;
    isContactEditorOpen.value = true;
  };

  const matchesSearch = (item: { name?: string | null; email: string }) => {
    if (!searchTerm.value) return true;

    return contains(item.name || item.email, searchTerm.value);
  };

  const sortByPrimary = (a: Contact, b: Contact) =>
    Number(b.is_primary_email) - Number(a.is_primary_email);

  const filteredExternal = computed(() =>
    (props.partner?.allContacts ?? [])
      .filter((c) => c.email && !c.internal_role && !isAlreadySelected(c.email))
      .filter(matchesSearch)
      .sort(sortByPrimary),
  );

  const filteredInternal = computed(() => {
    if (isInternalContactsEnabled.value) {
      return v2InternalOptions.value
        .filter((c) => c.email && !isAlreadySelected(c.email))
        .filter(matchesSearch)
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    return (props.partner?.allContacts ?? [])
      .filter((c) => c.internal_role && c.email && !isAlreadySelected(c.email))
      .filter(matchesSearch)
      .sort(sortByPrimary);
  });

  const filteredOrgUsers = computed(() =>
    (props.users ?? []).filter((u) => u.email && !isAlreadySelected(u.email)).filter(matchesSearch),
  );

  const optionGroups = computed(() =>
    [
      {
        key: 'customer' as ContactGroupKey,
        label: 'Customer Contacts',
        items: filteredExternal.value,
        isInternal: false,
      },
      {
        key: 'internal' as ContactGroupKey,
        label: 'Internal Contacts',
        items: filteredInternal.value,
        isInternal: true,
      },
      {
        key: 'team' as ContactGroupKey,
        label: 'Team Members',
        items: filteredOrgUsers.value,
        isInternal: false,
      },
    ]
      .filter((g) => !props.excludeGroups.includes(g.key))
      .filter((g) => g.items.length > 0),
  );

  const suggestedContact = computed(() => {
    if (!props.suggestContacts) return undefined;

    return (props.partner?.allContacts ?? [])
      .filter((c) => c.email && !c.internal_role && !isAlreadySelected(c.email))
      .sort(sortByPrimary)[0];
  });

  const visibleSuggestedContact = computed(() => {
    const suggested = suggestedContact.value;
    if (!suggested) return undefined;
    return matchesSearch(suggested) ? suggested : undefined;
  });

  const hasResults = computed(
    () => optionGroups.value.length > 0 || !!visibleSuggestedContact.value,
  );

  const focusInput = () => {
    nextTick(() => {
      const el = (inputRef.value as any)?.$el as HTMLElement | undefined;
      el?.focus();
    });
  };

  const tryAddEmail = (rawEmail: string, options?: { refocus?: boolean }): boolean => {
    const refocus = options?.refocus ?? true;
    const email = rawEmail.trim();

    if (!email) return false;

    if (!isEmailValid(email)) {
      hasError.value = false;
      nextTick(() => {
        hasError.value = true;
      });

      return false;
    }

    if (!isAlreadySelected(email)) {
      selectedEmails.value = [...(selectedEmails.value ?? []), email];
    }

    searchTerm.value = '';
    hasError.value = false;
    if (refocus) focusInput();

    return true;
  };

  const NAV_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Backspace', 'Delete'];

  const scrollActiveTagIntoView = () => {
    const inputEl = (inputRef.value as any)?.$el as HTMLElement | undefined;
    const container = inputEl?.closest('[data-slot="tags-input"]') as HTMLElement | null;
    const active = container?.querySelector('[data-state="active"]') as HTMLElement | null;

    active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' && !open.value) {
      e.preventDefault();
      openedByChevron.value = true;
      open.value = true;

      return;
    }

    if ((e.key === 'Enter' || e.key === ',' || e.key === ';') && searchTerm.value.trim()) {
      e.preventDefault();
      tryAddEmail(searchTerm.value);

      return;
    }

    if (e.key === 'Tab' && searchTerm.value.trim()) {
      const added = tryAddEmail(searchTerm.value, { refocus: false });
      if (!added) e.preventDefault();

      return;
    }

    if (NAV_KEYS.includes(e.key)) {
      nextTick(scrollActiveTagIntoView);
    }
  };

  const handleBlur = () => {
    // Prevent closing cc/bcc when user is interacting with dropdown
    if (open.value) return;

    nextTick(() => {
      if (searchTerm.value.trim()) {
        tryAddEmail(searchTerm.value, { refocus: false });
      }
    });
    emit('blur');
  };

  const handlePaste = (e: ClipboardEvent) => {
    const text = e.clipboardData?.getData('text');

    if (!text) return;
    if (!/[,;\n]/.test(text)) return;

    e.preventDefault();

    const parts = text
      .split(/[,;\n]/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    const merged = [...(selectedEmails.value ?? [])];
    const seen = new Set(merged.map((m) => m.toLowerCase()));
    let anyInvalid = false;

    for (const p of parts) {
      if (!isEmailValid(p)) {
        anyInvalid = true;
        continue;
      }
      if (seen.has(p.toLowerCase())) continue;

      merged.push(p);
      seen.add(p.toLowerCase());
    }

    selectedEmails.value = merged;
    searchTerm.value = '';

    if (anyInvalid) {
      hasError.value = false;
      nextTick(() => {
        hasError.value = true;
      });
    }

    focusInput();
  };

  const onPopoverUpdate = (isOpen: boolean) => {
    open.value = isOpen;
    openedByChevron.value = isOpen;

    if (openedByChevron.value) focusInput();
  };

  const onListboxSelect = () => {
    searchTerm.value = '';
    focusInput();

    if (openedByChevron.value) {
      nextTick(() => {
        open.value = true;
      });
    }
  };

  const onFocusOutside = (e: Event) => {
    if (openedByChevron.value) e.preventDefault();
  };

  watch(searchTerm, (v) => {
    if (v) {
      openedByChevron.value = false;
      open.value = true;
    }
    hasError.value = false;
  });
</script>

<template>
  <Popover :open="open" class="w-full" @update:open="onPopoverUpdate">
    <ListboxRoot v-model="selectedEmails" class="w-full" highlight-on-hover multiple>
      <PopoverAnchor class="inline-flex w-full">
        <TagsInput
          v-model="selectedEmails"
          v-bind="$attrs"
          :disabled="disabled"
          class="w-full max-h-25 overflow-y-auto"
          :class="invalid ? 'border-destructive' : ''"
          @click="focusInput"
          @keyup.escape.stop
        >
          <template v-for="{ email, contact, group } in taggedItems" :key="String(email)">
            <Tooltip v-if="!isEmailValid(String(email))" :delay-duration="1250">
              <TooltipTrigger as-child>
                <TagsInputItem :value="email" class="text-destructive">
                  <TagsInputItemText class="text-xs">{{ email }}</TagsInputItemText>
                  <TagsInputItemDelete />
                </TagsInputItem>
              </TooltipTrigger>
              <TooltipContent>Invalid email address</TooltipContent>
            </Tooltip>

            <HoverCard v-else-if="contact && !open" :open-delay="200" :close-delay="100">
              <HoverCardTrigger as-child>
                <TagsInputItem :value="email">
                  <TagsInputItemText class="text-xs">{{ email }}</TagsInputItemText>
                  <TagsInputItemDelete />
                </TagsInputItem>
              </HoverCardTrigger>

              <HoverCardContent class="w-auto p-3">
                <div class="flex items-start gap-3">
                  <UserAvatar class="mt-1" :user="contact" size="sm" />

                  <div class="flex flex-col gap-0.5 min-w-0">
                    <span class="font-semibold text-sm truncate">
                      {{ contact.name || 'Unknown' }}
                    </span>

                    <span class="text-xs text-muted-foreground truncate">
                      <i class="far fa-envelope text-2xs mr-1" />
                      {{ contact.email }}
                    </span>

                    <span
                      v-if="'phone' in contact && contact.phone"
                      class="text-xs text-muted-foreground"
                    >
                      <i class="far fa-phone text-2xs mr-1" />
                      {{ contact.phone }}
                    </span>

                    <Badge
                      v-if="group"
                      size="2xs"
                      :variant="GROUP_BADGE[group].variant"
                      class="mt-2 w-fit text-3xs"
                    >
                      {{ GROUP_BADGE[group].label }}
                    </Badge>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>

            <TagsInputItem v-else :value="email">
              <TagsInputItemText class="text-xs">{{ email }}</TagsInputItemText>
              <TagsInputItemDelete />

              <Tooltip v-if="partner && !contact" :delay-duration="1250">
                <TooltipTrigger as-child>
                  <button
                    type="button"
                    class="border-l px-2 h-full"
                    @click.stop.prevent="handleAddContact(String(email))"
                  >
                    <i class="fa far fa-circle-plus text-xs" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>Add this email to the customer's contacts</span>
                </TooltipContent>
              </Tooltip>
            </TagsInputItem>
          </template>

          <ListboxFilter v-model="searchTerm" as-child>
            <TagsInputInput
              ref="inputRef"
              :placeholder="placeholder"
              :class="{ 'text-destructive placeholder:text-destructive animate-shake': hasError }"
              @keydown="handleKeydown"
              @paste="handlePaste"
              @blur="handleBlur"
            />
          </ListboxFilter>

          <PopoverTrigger as-child>
            <Button
              size="icon-xs"
              variant="ghost"
              class="ml-auto self-start shrink-0 order-last"
              :disabled="disabled"
              @click.stop.prevent
              @keydown.enter.prevent.stop="onPopoverUpdate(!open)"
            >
              <i class="fas fa-chevron-down" />
            </Button>
          </PopoverTrigger>
        </TagsInput>
      </PopoverAnchor>

      <PopoverContent
        class="p-1 w-[var(--reka-popover-trigger-width)]"
        align="start"
        @open-auto-focus.prevent
        @focus-outside="onFocusOutside"
      >
        <ListboxContent
          class="max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto"
          tabindex="0"
        >
          <div
            v-if="!hasResults && searchTerm"
            class="flex items-center gap-x-1 px-2 py-1.5 text-center justify-center text-muted-foreground"
          >
            <span>No contacts found.</span>
            <Button
              variant="link"
              size="inline"
              class="px-0"
              @click.stop.prevent="tryAddEmail(searchTerm)"
            >
              Add {{ searchTerm }}
            </Button>
          </div>

          <ListboxGroup v-if="visibleSuggestedContact">
            <ListboxGroupLabel class="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Suggested
            </ListboxGroupLabel>

            <ListboxItem
              :value="visibleSuggestedContact.email"
              class="group data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-hidden data-[disabled]:opacity-50"
              @select="onListboxSelect"
            >
              <UserAvatar :user="visibleSuggestedContact" size="sm" />

              <div class="flex flex-col flex-1 min-w-0">
                <span class="truncate">{{ visibleSuggestedContact.name }}</span>
                <span class="text-muted-foreground truncate">
                  {{ visibleSuggestedContact.email }}
                </span>
              </div>

              <ListboxItemIndicator class="ml-auto inline-flex items-center justify-center">
                <i class="fas fa-check text-success" />
              </ListboxItemIndicator>
            </ListboxItem>
          </ListboxGroup>

          <ListboxGroup
            v-for="(group, idx) in optionGroups"
            :key="group.key"
            :class="(idx > 0 || visibleSuggestedContact) && 'border-t mt-1 pt-1'"
          >
            <ListboxGroupLabel class="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              {{ group.label }}
            </ListboxGroupLabel>

            <ListboxItem
              v-for="contact in group.items"
              :key="contact.email"
              :value="contact.email"
              class="group data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-hidden data-[disabled]:opacity-50"
              @select="onListboxSelect"
            >
              <UserAvatar :user="contact" size="sm" />

              <div class="flex flex-col flex-1 min-w-0">
                <span class="truncate">{{ contact.name }}</span>
                <span class="text-muted-foreground truncate">{{ contact.email }}</span>
              </div>

              <ListboxItemIndicator class="ml-auto inline-flex items-center justify-center">
                <i class="fas fa-check text-success" />
              </ListboxItemIndicator>
            </ListboxItem>
          </ListboxGroup>
        </ListboxContent>
      </PopoverContent>
    </ListboxRoot>
  </Popover>

  <ContactEditorDialog
    v-if="partner"
    v-model="isContactEditorOpen"
    :partner="partner"
    :email="newContactEmail"
    @saved="isContactEditorOpen = false"
    @close="isContactEditorOpen = false"
  />
</template>
